import { useState } from "react";
import { useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import {
  HiEye,
  HiEyeOff,
  HiLockClosed,
  HiMail,
  HiUser,
  HiUserCircle,
} from "react-icons/hi";
import { Link } from "react-router-dom";

import { RegisterCredentials } from "@/types/auth";

interface RegisterFormProps {
  error?: string;
  isLoading?: boolean;
  onGoogleLogin: () => void;
  onSubmit: (data: RegisterCredentials) => void;
}

export default function RegisterForm({
  error,
  isLoading = false,
  onGoogleLogin,
  onSubmit,
}: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<RegisterCredentials>();

  const password = watch("password");

  const handleFormSubmit = (data: RegisterCredentials) => {
    onSubmit(data);
  };

  return (
    <div className="card w-full max-w-md bg-base-100 shadow-xl">
      <div className="card-body">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
              <HiUserCircle className="h-10 w-10 text-primary-content" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-base-content">
            Create Account
          </h2>
          <p className="mt-2 text-base-content/70">
            Join us and start managing your dashboard
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-4">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Google Login Button */}
        {/* <button
          className="btn btn-outline mb-4 w-full normal-case"
          disabled={isLoading}
          onClick={onGoogleLogin}
          type="button"
        >
          <FcGoogle className="mr-2 h-5 w-5" />
          Continue with Google
        </button> */}

        <div className="divider">or</div>

        {/* Registration Form */}
        <form className="space-y-4" onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Name Fields */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="form-control">
              <label className="label">
                <span className="label-text">First Name</span>
              </label>
              <div className="relative">
                <input
                  {...register("firstName", {
                    minLength: {
                      message: "First name must be at least 2 characters",
                      value: 2,
                    },
                    required: "First name is required",
                  })}
                  className={`input input-bordered w-full pl-10 ${
                    errors.firstName ? "input-error" : ""
                  }`}
                  disabled={isLoading}
                  placeholder="John"
                  type="text"
                />
                <HiUser className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              </div>
              {errors.firstName && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.firstName.message}
                  </span>
                </label>
              )}
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Last Name</span>
              </label>
              <div className="relative">
                <input
                  {...register("lastName", {
                    minLength: {
                      message: "Last name must be at least 2 characters",
                      value: 2,
                    },
                    required: "Last name is required",
                  })}
                  className={`input input-bordered w-full pl-10 ${
                    errors.lastName ? "input-error" : ""
                  }`}
                  disabled={isLoading}
                  placeholder="Doe"
                  type="text"
                />
                <HiUser className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              </div>
              {errors.lastName && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.lastName.message}
                  </span>
                </label>
              )}
            </div>
          </div>

          {/* Email Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Email</span>
            </label>
            <div className="relative">
              <input
                {...register("email", {
                  pattern: {
                    message: "Invalid email address",
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  },
                  required: "Email is required",
                })}
                className={`input input-bordered w-full pl-10 ${
                  errors.email ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="john.doe@example.com"
                type="email"
              />
              <HiMail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
            </div>
            {errors.email && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.email.message}
                </span>
              </label>
            )}
          </div>

          {/* Password Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Password</span>
            </label>
            <div className="relative">
              <input
                {...register("password", {
                  minLength: {
                    message: "Password must be at least 8 characters",
                    value: 8,
                  },
                  pattern: {
                    message:
                      "Password must contain uppercase, lowercase, and number",
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  },
                  required: "Password is required",
                })}
                className={`input input-bordered w-full pl-10 pr-10 ${
                  errors.password ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
              />
              <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform"
                disabled={isLoading}
                onClick={() => setShowPassword(!showPassword)}
                type="button"
              >
                {showPassword ? (
                  <HiEyeOff className="h-5 w-5 text-base-content/50" />
                ) : (
                  <HiEye className="h-5 w-5 text-base-content/50" />
                )}
              </button>
            </div>
            {errors.password && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.password.message}
                </span>
              </label>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Confirm Password</span>
            </label>
            <div className="relative">
              <input
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
                className={`input input-bordered w-full pl-10 pr-10 ${
                  errors.confirmPassword ? "input-error" : ""
                }`}
                disabled={isLoading}
                placeholder="Confirm your password"
                type={showConfirmPassword ? "text" : "password"}
              />
              <HiLockClosed className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-base-content/50" />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 transform"
                disabled={isLoading}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                type="button"
              >
                {showConfirmPassword ? (
                  <HiEyeOff className="h-5 w-5 text-base-content/50" />
                ) : (
                  <HiEye className="h-5 w-5 text-base-content/50" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.confirmPassword.message}
                </span>
              </label>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="form-control">
            <label className="label cursor-pointer justify-start">
              <input
                {...register("acceptTerms", {
                  required: "You must accept the terms and conditions",
                })}
                className={`checkbox-primary checkbox mr-3 ${
                  errors.acceptTerms ? "checkbox-error" : ""
                }`}
                disabled={isLoading}
                type="checkbox"
              />
              <span className="label-text">
                I agree to the{" "}
                <Link className="link link-primary" to="/terms">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link className="link link-primary" to="/privacy">
                  Privacy Policy
                </Link>
              </span>
            </label>
            {errors.acceptTerms && (
              <label className="label">
                <span className="label-text-alt text-error">
                  {errors.acceptTerms.message}
                </span>
              </label>
            )}
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary w-full"
            disabled={isLoading}
            type="submit"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Creating Account...
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-6 text-center">
          <p className="text-base-content/70">
            Already have an account?{" "}
            <Link className="link link-primary font-semibold" to="/auth/login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
